import { type Entity, mapEntity } from '@api';
import { formatPriceDisplay } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import {
  mapEntityToPriceEntity,
  useLivePrice,
} from '@terminal/hooks/use-live-price';
import { getEntityPriceVenue } from '@terminal/services/price/price-service';
import { globalPriceFormatter } from '@terminal/utils/asset-price/assetPrice';
import { getPriceData } from '@terminal/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';
import { useTranslation } from 'react-i18next';

import { useSnakeMeta } from '../snakes/useSnake';
import { usePreliminaryPrice } from './usePreliminaryPrice';

export interface UsePriceDisplayReturn {
  isLoading: boolean;
  isValid: boolean;
  priceData: {
    price: ReturnType<typeof formatPriceDisplay>;
    mappedEntity: ReturnType<typeof mapEntity>;
    priceFormatter: (price: number) => string;
  } | null;
  time?: string;
}

export const usePriceDisplay = (entity: Entity): UsePriceDisplayReturn => {
  const { t } = useTranslation(['analyze']);
  const mappedEntity = mapEntity(entity);

  const { data: snakeMeta, isLoading: isSnakeMetaLoading } = useSnakeMeta(
    entity?.default_snake
  );
  const { data: preliminaryPrice } = usePreliminaryPrice(
    entity.subscribable_ticker ?? '',
    getEntityPriceVenue(entity)
  );

  const priceEntities = mapEntityToPriceEntity(entity, snakeMeta);
  const livePriceData = useLivePrice(priceEntities);
  const livePriceInfo = getPriceData({
    hoverData: undefined,
    livePriceData,
    entity: mappedEntity,
    ts: [],
    priceEntity: priceEntities[0],
  });

  const isLoading = isSnakeMetaLoading;

  const isValid = !!(
    (livePriceInfo || snakeMeta?.last_value !== undefined) &&
    snakeMeta?.last_timestamp
  );

  if (!isValid) {
    return {
      isLoading,
      isValid: false,
      priceData: null,
    };
  }

  const priceFormatter = (price: number) =>
    globalPriceFormatter({
      entity: mappedEntity,
      price,
      priceSuffix: '',
      shouldShowCurrency: false,
    });

  const price = formatPriceDisplay({
    exchange: mappedEntity.exchange.code,
    livePriceInfo,
    preliminaryPrice: preliminaryPrice?.data,
    meta: snakeMeta,
    isHovered: false,
    t,
  });

  return {
    isLoading,
    isValid: true,
    priceData: {
      price,
      mappedEntity,
      priceFormatter,
    },
    time: livePriceInfo?.time,
  };
};
